<template>
  <Layout>
    <PageHeader :title="pageTitle" :items="items"></PageHeader>
    <div class="row">
      <!-- Compose -->
      <div class="col-lg-6">
        <div class="card">
          <div class="card-body">
            <h5 class="mb-3">Message</h5>

            <b-form-group :label="`Sent to members as “${officialSenderName}”`">
              <b-form-textarea
                v-model="broadcastForm.body"
                rows="5"
                max-rows="12"
                placeholder="Type your message… a link on its own line gets a preview card."
              ></b-form-textarea>
            </b-form-group>

            <div v-if="broadcastForm.media" class="mb-3">
              <div class="small text-muted mb-1">
                Attached {{ broadcastForm.type }}
              </div>
              <img
                v-if="broadcastForm.type === 'image'"
                :src="broadcastForm.media.url"
                class="media-preview"
              />
              <video
                v-else
                :src="broadcastForm.media.url"
                class="media-preview"
                controls
              ></video>
              <button
                class="btn btn-sm btn-link text-danger d-block p-0 mt-1"
                @click="clearBroadcastMedia"
              >
                Remove attachment
              </button>
            </div>

            <label class="btn btn-sm btn-light mb-3">
              {{ broadcastUploading ? "Uploading…" : "📎 Attach image / video" }}
              <input
                type="file"
                accept="image/*,video/*"
                hidden
                :disabled="broadcastUploading"
                @change="onPickBroadcastFile"
              />
            </label>

            <div class="alert alert-light border small mb-3">
              Goes out immediately to every selected group as a separate
              message. There is no “unsend all” — each copy must be deleted
              individually.
            </div>

            <button
              class="btn btn-primary btn-block"
              :disabled="!canSend || broadcastSending"
              @click="confirmSend"
            >
              {{
                broadcastSending
                  ? "Sending…"
                  : `Send to ${broadcastSelected.length} group(s)`
              }}
            </button>
          </div>
        </div>

        <div v-if="broadcastResult" class="card">
          <div class="card-body">
            <h6>Last send</h6>
            <div v-if="broadcastResult.posted.length" class="small">
              <span class="text-success">Posted:</span>
              {{ broadcastResult.posted.map((p) => p.name).join(", ") }}
            </div>
            <div v-if="broadcastResult.failed.length" class="small mt-1">
              <span class="text-danger">Failed:</span>
              <span v-for="f in broadcastResult.failed" :key="f.communityId">
                {{ f.name || f.communityId }} ({{ f.reason }})
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Groups -->
      <div class="col-lg-6">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center mb-2">
              <h5 class="mb-0">Groups</h5>
              <span class="badge badge-primary ml-2">
                {{ broadcastSelected.length }} selected
              </span>
              <div class="ml-auto">
                <button class="btn btn-sm btn-link p-0" @click="selectAll">
                  Select all
                </button>
                <button
                  class="btn btn-sm btn-link p-0 ml-3"
                  @click="broadcastSelected = []"
                >
                  Clear
                </button>
              </div>
            </div>

            <b-form-input
              v-model="search"
              size="sm"
              class="mb-2"
              placeholder="Search groups…"
            ></b-form-input>

            <div class="d-flex align-items-center mb-2">
              <span class="text-muted small mr-auto">
                {{ showingRange(filteredGroups, broadcastPage) }}
              </span>
              <b-pagination
                v-if="filteredGroups.length > perPage"
                v-model="broadcastPage"
                :total-rows="filteredGroups.length"
                :per-page="perPage"
                size="sm"
                class="mb-0"
              ></b-pagination>
            </div>

            <div class="group-list border rounded p-2">
              <p v-if="!filteredGroups.length" class="text-muted small mb-0">
                No groups found.
              </p>
              <div v-for="g in pagedGroups" :key="g.id" class="form-check py-1">
                <input
                  :id="'bg-' + g.id"
                  v-model="broadcastSelected"
                  class="form-check-input"
                  type="checkbox"
                  :value="g.id"
                />
                <label class="form-check-label" :for="'bg-' + g.id">
                  {{ g.name }}
                  <span class="small text-muted">({{ g.type }})</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import MixinRequest from "@/mixins/request";
import communityChatMixin from "@/mixins/ModuleJs/community-chat";

export default {
  data() {
    return {
      pageTitle: "Broadcast",
      items: [
        { text: "Home", href: "/" },
        { text: "Community Chat", href: "/community-chat" },
        { text: "Broadcast" },
      ],
      search: "",
      broadcastPage: 1,
    };
  },
  mixins: [communityChatMixin, MixinRequest],
  components: { Layout, PageHeader },
  watch: {
    search() {
      this.broadcastPage = 1;
    },
  },
  computed: {
    // Overrides the mixin's filteredGroups (which filters the groups-with-messages
    // list) — here the source is the postable-groups list.
    filteredGroups() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.postableGroups;
      return this.postableGroups.filter((g) =>
        g.name.toLowerCase().includes(q)
      );
    },
    // Only the current page is rendered; selection still spans the whole filter.
    pagedGroups() {
      return this.paginate(this.filteredGroups, this.broadcastPage);
    },
    canSend() {
      const hasContent =
        (this.broadcastForm.body || "").trim() || this.broadcastForm.media;
      return Boolean(hasContent) && this.broadcastSelected.length > 0;
    },
  },
  async created() {
    await this.fetchPostableGroups();
  },
  methods: {
    // Only the groups currently visible under the search filter, so "select
    // all" can't silently pick groups the user isn't looking at.
    selectAll() {
      const ids = this.filteredGroups.map((g) => g.id);
      this.broadcastSelected = [
        ...new Set([...this.broadcastSelected, ...ids]),
      ];
    },
    confirmSend() {
      const names = this.postableGroups
        .filter((g) => this.broadcastSelected.includes(g.id))
        .map((g) => g.name);
      this.$bvModal
        .msgBoxConfirm(
          `Send this message to ${names.length} group(s)?\n\n${names.join(", ")}`,
          { title: "Confirm broadcast", okTitle: "Send", cancelTitle: "Cancel" }
        )
        .then((ok) => {
          if (ok) this.sendBroadcast();
        });
    },
  },
};
</script>

<style scoped>
.group-list {
  max-height: 420px;
  overflow-y: auto;
}
.media-preview {
  max-width: 220px;
  max-height: 160px;
  object-fit: contain;
  border-radius: 6px;
  display: block;
}
</style>
